import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import AddPointUsePolicyModal from "./components/AddPointUsePolicyModal/AddPointUsePolicyModal";

interface PointUsePolicyListState {
    policies: any[];
    loading: boolean;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    isAddModalOpen: boolean;
    editPolicy: any | null;
    deleteConfirmOpen: boolean;
    policyToDelete: any | null;
    deleting: boolean;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_point_use_policy_page_size";

class PointUsePolicyListScreenClass extends Component<any, PointUsePolicyListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            policies: [],
            loading: false,
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            isAddModalOpen: false,
            editPolicy: null,
            deleteConfirmOpen: false,
            policyToDelete: null,
            deleting: false,
        };
    }

    componentDidMount() {
        this.loadPolicies();
    }

    loadPolicies = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
            };

            const res = await request("PointUsePolicy.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const policies = (pagedList.items || []).map((policy: any) => ({
                    seqId: policy.seqId,
                    policyName: policy.policyName || "-",
                    useUnit: policy.useUnit || 0,
                    minimumUseAmount: policy.minimumUseAmount || 0,
                    maximumUseAmount: policy.maximumUseAmount || null,
                    isActive: policy.isActive,
                    isDefault: policy.isDefault,
                    tenantName: policy.tenantName || "전체",
                    crtDt: policy.crtDt ? new Date(policy.crtDt).toLocaleDateString('ko-KR') : "-",
                }));

                this.setState({
                    policies,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({
                    policies: [],
                    totalCount: 0,
                    loading: false
                });
            }
        } catch (error: any) {
            console.error("포인트 사용 정책 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadPolicies();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadPolicies();
        });
    };

    handleOpenAddModal = () => {
        this.setState({ isAddModalOpen: true, editPolicy: null });
    };

    handleCloseAddModal = () => {
        this.setState({ isAddModalOpen: false, editPolicy: null });
    };

    handlePolicyAdded = () => {
        this.loadPolicies();
    };

    handleOpenEditModal = (policy: any) => {
        this.setState({ isAddModalOpen: true, editPolicy: policy });
    };

    handleOpenDeleteConfirm = (policy: any) => {
        this.setState({ deleteConfirmOpen: true, policyToDelete: policy });
    };

    handleCloseDeleteConfirm = () => {
        this.setState({ deleteConfirmOpen: false, policyToDelete: null });
    };

    handleDelete = async () => {
        if (!this.state.policyToDelete) return;

        this.setState({ deleting: true });

        try {
            await request("PointUsePolicy.delete", {
                pathParams: { id: this.state.policyToDelete.seqId },
            });

            this.setState({
                deleteConfirmOpen: false,
                policyToDelete: null,
                deleting: false,
            });

            this.loadPolicies();
        } catch (error: any) {
            console.error("포인트 사용 정책 삭제 실패:", error);
            alert(error.response?.data?.message || "정책 삭제에 실패했습니다. 사용자가 할당된 정책은 삭제할 수 없습니다.");
            this.setState({ deleting: false });
        }
    };

    render() {
        const { loading, totalCount, pageSize, pageNumber } = this.state;

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">policy</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        포인트 사용 정책 목록 ( POINT USE POLICY LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" onClick={this.handleOpenAddModal}>
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                        add
                                    </Icon>
                                    정책 추가
                                </MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        ) : this.state.policies.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    등록된 포인트 사용 정책이 없습니다.
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {this.state.policies.map((policy) => {
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 }
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };

                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%',
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%',
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={policy.seqId}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 6,
                                                        },
                                                    }}
                                                >
                                                    <MDBox p={2.5} display="flex" flexDirection="column" height="100%">
                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                            <MDTypography variant="h6" fontWeight="medium">
                                                                {policy.policyName}
                                                            </MDTypography>
                                                            <MDBox display="flex" gap={0.5}>
                                                                {policy.isDefault && (
                                                                    <Chip
                                                                        label="기본"
                                                                        color="primary"
                                                                        size="small"
                                                                        sx={{
                                                                            fontWeight: "medium",
                                                                            backgroundColor: "#2196F3",
                                                                            color: "#ffffff",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                                <Chip
                                                                    label={policy.isActive ? "활성" : "비활성"}
                                                                    color={policy.isActive ? "success" : "default"}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: "medium",
                                                                        ...(!policy.isActive && {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 255, 255, 0.12)" : undefined,
                                                                            color: this.props.darkMode ? "#ffffff" : undefined,
                                                                        }),
                                                                        ...(policy.isActive && {
                                                                            backgroundColor: "#4CAF50",
                                                                            color: "#ffffff",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                            },
                                                                        }),
                                                                    }}
                                                                />
                                                            </MDBox>
                                                        </MDBox>

                                                        <Divider sx={{ my: 2 }} />

                                                        <MDBox mb={1.5} flexGrow={1}>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                사용 단위: <strong>{policy.useUnit}P</strong>
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                최소 사용: <strong>{policy.minimumUseAmount}P</strong>
                                                            </MDTypography>
                                                            {policy.maximumUseAmount && (
                                                                <MDTypography variant="caption" color="text" display="block">
                                                                    최대 사용: <strong>{policy.maximumUseAmount}P</strong>
                                                                </MDTypography>
                                                            )}
                                                        </MDBox>

                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                            <MDBox>
                                                                <MDTypography variant="caption" color="text">
                                                                    {policy.tenantName}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <MDBox display="flex" gap={0.5}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.handleOpenEditModal(policy);
                                                                    }}
                                                                    sx={{
                                                                        color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                        "&:hover": {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                                            color: "#49a3f1",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Icon fontSize="small">edit</Icon>
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.handleOpenDeleteConfirm(policy);
                                                                    }}
                                                                    sx={{
                                                                        color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                        "&:hover": {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                                            color: "#f44336",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Icon fontSize="small">delete</Icon>
                                                                </IconButton>
                                                            </MDBox>
                                                        </MDBox>

                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center" alignItems="center" gap={2}>
                                        <Pagination
                                            count={Math.ceil(totalCount / pageSize)}
                                            page={pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />
                <AddPointUsePolicyModal
                    open={this.state.isAddModalOpen}
                    onClose={this.handleCloseAddModal}
                    onSuccess={this.handlePolicyAdded}
                    darkMode={this.props.darkMode}
                    editPolicy={this.state.editPolicy}
                />
                <Dialog
                    open={this.state.deleteConfirmOpen}
                    onClose={this.handleCloseDeleteConfirm}
                    PaperProps={{
                        sx: {
                            backgroundColor: this.props.darkMode ? '#1a2035' : '#ffffff',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle sx={{ color: this.props.darkMode ? '#ffffff' : 'inherit' }}>
                        정책 삭제 확인
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit' }}>
                            정말로 "{this.state.policyToDelete?.policyName}" 정책을 삭제하시겠습니까?
                            <br />
                            <strong>사용자가 할당된 정책은 삭제할 수 없습니다.</strong>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleCloseDeleteConfirm} color="secondary" disabled={this.state.deleting}>
                            취소
                        </MDButton>
                        <MDButton onClick={this.handleDelete} color="error" disabled={this.state.deleting}>
                            {this.state.deleting ? "삭제 중..." : "삭제"}
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

function PointUsePolicyListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <PointUsePolicyListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default PointUsePolicyListScreen;
